/* KCI Event Tagging › Tagged Orders (KCI_API_DOC §6, mockup Tagged Orders).
   The SLA payoff view: on-time % with vs. without KCI-tagged orders, and the
   tagged-orders table with the dual-EDD columns (System EDD vs KCI-Impacted EDD).
   Analysis mode (all / include / exclude) frames which on-time number is the answer;
   the row listing is the KCI-tagged set this module owns (non-tagged listings ride
   the main Orders search per §6.3). */
import React, { useEffect, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import {
  Box, Grid, Typography, TextField, Tooltip,
} from '@mui/material'
import PageTitle from '../../components/PageTitle'
import DateRangePicker from '../../components/DateRangePicker'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import { FilterCard, FilterCardContent, FilterRow, FilterField, FieldLabel, FilterActions, ApplyButton, ResetButton } from '../../common/FilterBar'
import { useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'
import { useTaggedOrdersQuery, useTaggedOrdersAnalysisQuery } from '../../redux/kciApiSlice'
import { directionShort } from './kciConstants'
import { useForm } from 'react-hook-form'
import { FormProvider, RHFSearchableSelectField, RHFMultipleSelectField } from '../../common/hook-form'
import { StatTile, gridSx, gridWrapSx, CellLine, CellStack, MUTED, errMsg, COURIER_LIST_FILTER, kciCourierParents, kciServiceTypes, kciAccountOptions, toKey, parseKey } from './kciUi'

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10)
const today = () => new Date().toISOString().slice(0, 10)
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—')
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`)

// ── Colored pills (D2C Forward Shipment ZoneBadge / PaymentBadge style) ────────
const Badge = ({ tone, children, sx }) => (
  <Box component='span' sx={{
    display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: '6px',
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase',
    color: tone.fg, backgroundColor: tone.bg, whiteSpace: 'nowrap', ...sx,
  }}>{children}</Box>
)
const dirTone = (v) => ({
  FORWARD: { bg: '#e8f1fc', fg: '#1565c0' },
  REVERSE: { bg: '#fdf2e5', fg: '#b45309' },
  BOTH: { bg: '#e4f3f1', fg: '#0f766e' },
}[v] || { bg: '#eef1f5', fg: '#475569' })
const DirectionBadge = ({ value }) => <Badge tone={dirTone(value)}>{directionShort(value)}</Badge>

const statusTone = (v) => {
  const s = (v || '').toLowerCase()
  if (s.includes('deliver')) return { bg: '#e7f6ef', fg: '#1b7f5c' }
  if (s.includes('rto') || s.includes('return')) return { bg: '#fdf2e5', fg: '#b45309' }
  if (s.includes('ndr') || s.includes('exception') || s.includes('fail') || s.includes('undeliver')) return { bg: '#fdecea', fg: '#c62828' }
  if (s.includes('transit') || s.includes('pickup') || s.includes('picked') || s.includes('out for') || s.includes('ship')) return { bg: '#e8f1fc', fg: '#1565c0' }
  return { bg: '#eef1f5', fg: '#475569' }
}
const StatusBadge = ({ value }) => (value
  ? <Tooltip title={value.replace(/_/g, ' ')}><Badge tone={statusTone(value)} sx={{ height: 20, fontSize: '10px' }}>{value}</Badge></Tooltip>
  : <CellLine muted>—</CellLine>)

export default function TaggedOrders() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_FILTER)
  // dropdown drafts live in react-hook-form (shared RHFSearchableSelectField);
  // dates + search stay local. `applied` drives the API (set on "Filter").
  const methods = useForm({ defaultValues: { direction: '', courierParent: '', courierServiceType: [], courierCode: '' } })
  const { handleSubmit } = methods
  const courierParent = methods.watch('courierParent')
  const courierServiceType = methods.watch('courierServiceType')
  const courierCode = methods.watch('courierCode')
  const [from, setFrom] = useState(daysAgo(30))
  const [to, setTo] = useState(today())
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState({ from: daysAgo(30), to: today(), direction: '', courierCode: '', search: '' })
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(50)

  const applyFilters = (data) => { setApplied({ from, to, direction: data.direction, courierCode: data.courierCode, search }); setPage(0) }
  const resetFilters = () => {
    const d = daysAgo(30), t = today()
    methods.reset({ direction: '', courierParent: '', courierServiceType: [], courierCode: '' })
    setFrom(d); setTo(t); setSearch('')
    setApplied({ from: d, to: t, direction: '', courierCode: '', search: '' }); setPage(0)
  }

  const analysisParams = useMemo(() => {
    const p = { from: applied.from, to: applied.to }
    if (applied.direction) p.direction = applied.direction
    return p
  }, [applied])
  const { data: analysis, isError: analysisError, error: analysisErrObj } = useTaggedOrdersAnalysisQuery(analysisParams)

  const listParams = useMemo(() => {
    const p = { from: applied.from, to: applied.to, page: page + 1, size: sizePerPage }
    if (applied.direction) p.direction = applied.direction
    if (applied.courierCode) p.courierCode = applied.courierCode
    if (applied.search.trim()) p.search = applied.search.trim()
    return p
  }, [applied, page, sizePerPage])
  const { data, isFetching, isError, error } = useTaggedOrdersQuery(listParams)
  const rows = data?.orders || []
  const rowCount = data?.totalRecords ?? rows.length

  useEffect(() => {
    if (isError) enqueueSnackbar(errMsg(error, 'Could not load tagged orders.'), { variant: 'error' })
  }, [isError, error, enqueueSnackbar])
  useEffect(() => {
    if (analysisError) enqueueSnackbar(errMsg(analysisErrObj, 'Could not load Delivery SLA analysis.'), { variant: 'error' })
  }, [analysisError, analysisErrObj, enqueueSnackbar])

  const columns = [
    {
      field: 'order', headerName: 'Order', minWidth: 180, flex: 1.3, sortable: false,
      renderCell: (p) => (
        <CellStack>
          <CellLine bold>{p.row.orderReference || '—'}</CellLine>
          <CellLine muted ellipsis>AWB: {p.row.awbNumber || '—'}</CellLine>
        </CellStack>
      ),
    },
    {
      field: 'courier', headerName: 'Courier', minWidth: 190, flex: 1, sortable: false,
      renderCell: (p) => (
        <CellStack>
          <CellLine bold>{p.row.courierCode || '—'}</CellLine>
          <CellLine muted>Pickup {p.row.pickupPincode || '—'} · Drop {p.row.dropPincode ?? p.row.pincode ?? '—'}</CellLine>
        </CellStack>
      ),
    },
    { field: 'direction', headerName: 'Direction', minWidth: 100, sortable: false, renderCell: (p) => <DirectionBadge value={p.row.direction} /> },
    {
      // the global listing spans events, so the raw id told the reader nothing — lead with
      // the title and keep the id underneath for support lookups
      field: 'eventId', headerName: 'Linked Event', minWidth: 190, flex: 1, sortable: false,
      renderCell: (p) => (
        <CellStack>
          <CellLine bold ellipsis>{p.row.eventTitle || '—'}</CellLine>
          <CellLine muted ellipsis>{p.row.eventId || '—'}</CellLine>
        </CellStack>
      ),
    },
    { field: 'systemEdd', headerName: 'System EDD', minWidth: 110, sortable: false, renderCell: (p) => <CellLine>{fmtDate(p.row.systemEdd)}</CellLine> },
    {
      field: 'kciImpactedEdd', headerName: 'KCI-Impacted EDD', minWidth: 150, sortable: false,
      renderCell: (p) => p.row.kciImpactedEdd
        ? (
          <Box component='span' sx={{
            display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: '6px',
            fontSize: '11.5px', fontWeight: 700, color: '#6D28D9', backgroundColor: '#F1ECFD', whiteSpace: 'nowrap',
          }}>{fmtDate(p.row.kciImpactedEdd)}</Box>
        )
        : <CellLine muted>—</CellLine>,
    },
    { field: 'orderStatus', headerName: 'Status', minWidth: 150, sortable: false, renderCell: (p) => <StatusBadge value={p.row.orderStatus} /> },
  ]

  return (
    <Box>
      <PageTitle>KCI Management · Tagged Orders</PageTitle>

      {/* Delivery-SLA cards — KCI-tagged orders DELIVERED in the window only
          (actualDeliveryTime + EDD). Network-wide baselines live on the KCI Dashboard;
          serving them here forced a full delivered-orders scan (minutes on prod). */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#212B36' }}>Delivery SLA — KCI-tagged orders delivered in this window</Typography>
        <Typography sx={{ fontSize: 11.5, color: MUTED }}>
          On-time % is measured on tagged shipments that actually delivered in the selected dates — this is separate from the tagged-orders list below, so the counts won't match.
        </Typography>
      </Box>
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        <Grid item xs={6} md={3}><StatTile label='KCI-tagged (delivered)' value={analysis?.kciTotal?.toLocaleString?.() ?? '—'} accent='#6d28d9' /></Grid>
        <Grid item xs={6} md={3}><StatTile label='On-time % — KCI impacted' value={pct(analysis?.onTimePctKciOnly)} accent='#16a34a' /></Grid>
        <Grid item xs={6} md={3}><StatTile label='Delivered late (KCI)' value={analysis?.kciLate?.toLocaleString?.() ?? '—'} accent='#dc2626' /></Grid>
      </Grid>
      {analysis && !analysis.kciTotal && (
        <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 2 }}>
          No KCI-tagged shipments delivered in this window yet, so on-time % is blank — it fills in as tagged orders deliver. To see real SLA numbers, pick a range with completed deliveries.
        </Typography>
      )}

      <FilterCard>
        <FilterCardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(applyFilters)}>
            <FilterRow>
              <FilterField sx={{ flex: '1 1 220px' }}>
                <FieldLabel>Search AWB / Order ID</FieldLabel>
                <TextField size='small' fullWidth placeholder='Exact match — overrides other filters' value={search}
                  onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(applyFilters)() }} />
              </FilterField>
              <FilterField>
                <FieldLabel>Direction</FieldLabel>
                <RHFSearchableSelectField name='direction' label='' fullWidth placeholder='All directions' options={[
                  { value: 'FORWARD', label: 'Forward only' },
                  { value: 'REVERSE', label: 'Reverse only' },
                ]} />
              </FilterField>
              <FilterField>
                <FieldLabel>Courier</FieldLabel>
                <RHFSearchableSelectField name='courierParent' label='' fullWidth placeholder='All couriers'
                  options={kciCourierParents(couriers)} />
              </FilterField>
              {courierParent && (
                <FilterField>
                  <FieldLabel>Service Type</FieldLabel>
                  <RHFMultipleSelectField name='courierServiceType' label='' width='100%' search outputFormat='array'
                    placeholder='All service types' options={kciServiceTypes(couriers, courierParent)} />
                </FilterField>
              )}
              {courierParent && Array.isArray(courierServiceType) && courierServiceType.length > 0 && (
                <FilterField>
                  <FieldLabel>Account</FieldLabel>
                  <RHFSearchableSelectField name='courierCode' label='' fullWidth placeholder='All accounts'
                    options={(() => {
                      const opts = kciAccountOptions(couriers, courierParent, courierServiceType)
                      if (courierCode && !opts.some((o) => o.value === courierCode)) {
                        const c = couriers.find((x) => x.cpAccountCode === courierCode)
                        opts.push({ value: courierCode, label: c && c.name ? `${c.name} (${courierCode})` : courierCode })
                      }
                      return opts
                    })()} />
                </FilterField>
              )}
              <FilterField sx={{ flex: '0 0 280px' }}>
                <FieldLabel>Date range</FieldLabel>
                <DateRangePicker showTextBox size='small' label='' placeholder='Select date range' maxDate={new Date()}
                  defaultRange={from && to ? [{ startDate: parseKey(from), endDate: parseKey(to), key: 'selection' }] : []}
                  onChange={(range) => {
                    if (range?.length && range[0].startDate && range[0].endDate) {
                      setFrom(toKey(range[0].startDate)); setTo(toKey(range[0].endDate))
                    }
                  }} />
              </FilterField>
              <FilterActions>
                <ApplyButton type='submit' variant='contained' size='small'>Filter</ApplyButton>
                <ResetButton type='button' variant='contained' size='small' onClick={resetFilters}>Reset</ResetButton>
              </FilterActions>
            </FilterRow>
          </FormProvider>
        </FilterCardContent>
      </FilterCard>

      <Typography variant='caption' sx={{ display: 'block', mb: 1, color: MUTED }}>
        <b>System EDD</b> is Prozo's standard estimate. <b>KCI-Impacted EDD</b> is the floating date shown to the customer while the event is live.
      </Typography>
      {/* overflowX:auto + inner minWidth = the whole table scrolls left/right on narrow
          viewports instead of squeezing/clipping the last (Status) column. */}
      <Box sx={{ ...gridWrapSx, overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1000 }}>
          <ProzoDataGrid
            columns={columns} rows={rows} getRowId={(r) => r.awbNumber || r.orderReference} rowCount={rowCount}
            paginationMode='server' page={page} setPage={setPage}
            sizePerPage={sizePerPage} setSizePerPage={setSizePerPage}
            loading={isFetching} getRowHeight={() => 'auto'} sx={gridSx}
          />
        </Box>
      </Box>
    </Box>
  )
}
